import { useState } from "react";
import { Download, FileArchive, FileImage, FileSpreadsheet, FileText, File as FileIcon, ExternalLink, Eye, EyeOff, Maximize2 } from "lucide-react";
import { fileUrl } from "@/lib/api";
import { fmtBytes } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

function iconFor(ct = "", name = "") {
  if (ct.startsWith("image/")) return FileImage;
  if (ct.includes("pdf")) return FileText;
  if (ct.includes("sheet") || ct.includes("csv") || /\.(xlsx?|csv)$/i.test(name)) return FileSpreadsheet;
  if (ct.includes("zip") || ct.includes("compressed") || /\.(zip|rar|7z|gz)$/i.test(name)) return FileArchive;
  if (ct.startsWith("text/") || ct.includes("word") || ct.includes("document")) return FileText;
  return FileIcon;
}

const isImage = (f) => (f.content_type || "").startsWith("image/");
const isPdf = (f) => (f.content_type || "").includes("pdf") || /\.pdf$/i.test(f.filename || "");

function FileActions({ f }) {
  return (
    <div className="flex items-center gap-0.5">
      <a href={fileUrl(f.id)} target="_blank" rel="noreferrer" className="p-1 text-muted-foreground transition-colors hover:text-foreground" title="Open in new tab" data-testid={`attachment-open-${f.id}`}><ExternalLink className="h-3.5 w-3.5" /></a>
      <a href={fileUrl(f.id, true)} className="p-1 text-muted-foreground transition-colors hover:text-foreground" title="Download" data-testid={`attachment-download-${f.id}`}><Download className="h-3.5 w-3.5" /></a>
    </div>
  );
}

function FileMeta({ f, children }) {
  return (
    <div className="flex items-center gap-2 border-b border-border px-3 py-1.5">
      <div className="min-w-0 flex-1">
        <div className="truncate text-xs font-semibold" title={f.filename}>{f.filename}</div>
        <div className="mono text-[10px] text-muted-foreground">{f.content_type} · {fmtBytes(f.size)}</div>
      </div>
      {children}
      <FileActions f={f} />
    </div>
  );
}

function ImagePreview({ f }) {
  const [zoom, setZoom] = useState(false);
  const [broken, setBroken] = useState(false);
  return (
    <li className="surface overflow-hidden" data-testid={`attachment-${f.id}`}>
      <FileMeta f={f}>
        <button type="button" onClick={() => setZoom(true)} className="p-1 text-muted-foreground transition-colors hover:text-foreground" title="View full size" data-testid={`attachment-zoom-${f.id}`}><Maximize2 className="h-3.5 w-3.5" /></button>
      </FileMeta>
      {broken ? <div className="p-3 text-xs text-muted-foreground">Preview unavailable — use Open or Download.</div> : (
        <button type="button" onClick={() => setZoom(true)} className="block min-h-12 w-full bg-muted/30 p-2 text-left" data-testid={`attachment-image-${f.id}`}>
          <img src={fileUrl(f.id)} alt={f.filename} loading="lazy" onError={() => setBroken(true)} className="mx-auto max-h-72 w-auto max-w-full object-contain" />
        </button>
      )}
      {zoom && (
        <Dialog open onOpenChange={(o) => !o && setZoom(false)}>
          <DialogContent className="max-h-[92vh] max-w-5xl overflow-auto p-2" data-testid={`attachment-lightbox-${f.id}`}>
            <DialogTitle className="sr-only">{f.filename}</DialogTitle>
            <img src={fileUrl(f.id)} alt={f.filename} className="mx-auto max-h-[85vh] w-auto max-w-full object-contain" />
            <div className="mt-2 flex items-center justify-between px-1 text-xs"><span className="mono truncate">{f.filename}</span><FileActions f={f} /></div>
          </DialogContent>
        </Dialog>
      )}
    </li>
  );
}

const pdfInlineSupported = () => typeof navigator === "undefined" || navigator.pdfViewerEnabled !== false;

function PdfPreview({ f, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const supported = pdfInlineSupported();
  return (
    <li className="surface overflow-hidden" data-testid={`attachment-${f.id}`}>
      <FileMeta f={f}>
        {supported && <button type="button" onClick={() => setOpen(!open)} className="flex items-center gap-1 p-1 text-[11px] text-muted-foreground transition-colors hover:text-foreground" data-testid={`attachment-pdf-toggle-${f.id}`}>{open ? <><EyeOff className="h-3.5 w-3.5" /> Hide</> : <><Eye className="h-3.5 w-3.5" /> Preview</>}</button>}
      </FileMeta>
      {!supported && <div className="p-3 text-xs text-muted-foreground" data-testid={`attachment-pdf-fallback-${f.id}`}>This browser can't show PDFs inline — <a href={fileUrl(f.id)} target="_blank" rel="noreferrer" className="text-primary underline">open it in a new tab</a>.</div>}
      {supported && open && (
        <object data={`${fileUrl(f.id)}#toolbar=1&view=FitH`} type="application/pdf" className="h-[520px] w-full bg-muted/30" aria-label={f.filename} data-testid={`attachment-pdf-${f.id}`}>
          <div className="p-3 text-xs text-muted-foreground">Your browser can't show PDFs inline — <a href={fileUrl(f.id)} target="_blank" rel="noreferrer" className="text-primary underline">open it in a new tab</a>.</div>
        </object>
      )}
    </li>
  );
}

function FileRow({ f }) {
  const Icon = iconFor(f.content_type, f.filename);
  return (
    <li className="surface group flex items-stretch overflow-hidden" data-testid={`attachment-${f.id}`}>
      <a href={fileUrl(f.id)} target="_blank" rel="noreferrer" className="grid w-14 shrink-0 place-items-center border-r border-border bg-muted/40"><Icon className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} /></a>
      <div className="min-w-0 flex-1 px-3 py-2">
        <div className="truncate text-xs font-semibold" title={f.filename}>{f.filename}</div>
        <div className="mono text-[10px] text-muted-foreground">{f.content_type} · {fmtBytes(f.size)}</div>
      </div>
      <div className="flex items-center pr-2"><FileActions f={f} /></div>
    </li>
  );
}

export function AttachmentList({ files, className, large = false }) {
  const media = files.filter((f) => isImage(f) || isPdf(f));
  const others = files.filter((f) => !isImage(f) && !isPdf(f));
  const pdfOpen = media.filter(isPdf).length <= 2;
  return (
    <div className={cn("space-y-2", className)} data-testid="attachment-list">
      {media.length > 0 && (
        <ul className={cn("grid gap-2", large ? "grid-cols-1 xl:grid-cols-2" : "grid-cols-1")} data-testid="attachment-previews">
          {media.map((f) => (isImage(f) ? <ImagePreview key={f.id} f={f} /> : <PdfPreview key={f.id} f={f} defaultOpen={pdfOpen} />))}
        </ul>
      )}
      {others.length > 0 && (
        <ul className={cn("grid gap-2", large ? "grid-cols-1 sm:grid-cols-2 xl:grid-cols-3" : "grid-cols-1 sm:grid-cols-2")}>
          {others.map((f) => <FileRow key={f.id} f={f} />)}
        </ul>
      )}
    </div>
  );
}
